import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Menu } from "lucide-react";
import NotificationBell from "./NotificationBell";
import { apiClient } from "../apiclient/apiclient";
import ConfirmDialog from "../components/ConfirmDialog";

const Header = ({ onMenuClick }) => {
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const ref = useRef(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  /* ================= LOAD COMPANY ================= */
  useEffect(() => {
    const loadCompany = async () => {
      try {
        const res = await apiClient.get("/settings/company");
        setCompany(res.data);
      } catch (err) {
        console.error("Failed to load company");
      }
    };
    loadCompany();
  }, []);

  /* ================= CLOSE DROPDOWN ================= */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const stored = JSON.parse(
    localStorage.getItem("agent") ||
      localStorage.getItem("user") ||
      localStorage.getItem("auth") ||
      "{}",
  );

  const displayName =
    stored?.name || stored?.firstName || stored?.email || "Admin";

  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-blue-950 border-b border-blue-900 z-50 shadow-md">
      <div className="h-full flex items-center justify-between px-3">
        {/* LEFT: BRAND (ALIGNED WITH SIDEBAR) */}
        <div className="flex items-center gap-4 ">
          {/* Mobile Menu */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-slate-200 hover:bg-amber-500/10"
          >
            <Menu size={22} />
          </button>

          {/* LOGO */}
          <img
            src={
              company?.logo
                ? `${process.env.REACT_APP_IMAGE_URL}/${company.logo}`
                : `${process.env.PUBLIC_URL}/starnutritionlogo.png`
            }
            alt="Company Logo"
            className="h-10 w-20 object-contain rounded-md bg-white p-1 shadow-sm"
          />

          <span className="sm:text-lg text-base font-semibold text-white">
            {company?.companyName || "Star Nutrition"}
          </span>
        </div>

        {/* RIGHT: USER */}
        <div className="flex items-center gap-3">
          <NotificationBell />

          <div className="relative" ref={ref}>
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-orange-500 text-white text-center leading-[34px] font-bold text-base shadow-sm">
                {initials}
              </div>

              <span className="hidden md:inline text-xs font-semibold text-white">
                {displayName}
              </span>

              <ChevronDown size={14} className="text-white/70" />
            </button>

            {open && (
              <>
                <style>{`
                  @keyframes dropdownShow {
                    from {
                      opacity: 0;
                      transform: translateY(-8px) scale(0.95);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0) scale(1);
                    }
                  }
                  .animate-dropdown {
                    animation: dropdownShow 0.15s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    transform-origin: top right;
                  }
                `}</style>
                <div className="absolute right-0 top-12 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50 animate-dropdown">
                  {/* POINTER */}
                  <div
                    className="absolute -top-1.5 right-4 w-3 h-3 bg-white border-t border-l border-gray-200"
                    style={{
                      transform: "rotate(45deg)",
                    }}
                  />
                  <button
                    onClick={() => setShowLogoutDialog(true)}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 font-semibold transition-colors rounded-md relative z-10"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showLogoutDialog}
        title="Logout"
        description="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={() => {
          setShowLogoutDialog(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutDialog(false)}
        variant="danger"
      />
    </header>
  );
};

export default Header;
